import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { IconButton, ListItem } from "@mui/material";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import GetAppRoundedIcon from "@mui/icons-material/GetAppRounded";
import StarIcon from "@mui/icons-material/Star";
import FilePreview from "./FilePreview";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const getItemStyle = (isDragging, draggableStyle, index) => ({
  userSelect: "none",
  borderRadius: "10px",
  background: isDragging ? "#fff5f5" : index === 0 ? "#f8fbff" : "#f3f9ff",
  width: "140px",
  height: "140px",
  minWidth: "140px",
  maxWidth: "140px",
  position: "relative",
  ...draggableStyle,
  padding: "0px",
  overflow: "hidden",
  boxShadow: "0 0 5px #5353532b",
});

const getListStyle = () => ({
  marginTop: "10px",
  paddingBottom: "10px",
  display: "flex",
  justifyContent: "start",
  overflow: "auto",
  flexDirection: "row",
  gap: "0.5rem",
});

const Sortable = ({ itemsList, errors, onDelete, onSort, disabled, isRtl }) => {
  const [items, setItems] = useState(itemsList);
  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const sortedItems = reorder(
      items,
      result.source.index,
      result.destination.index
    );
    onSort(sortedItems);
    setItems(sortedItems);
  };
  useEffect(() => {
    setItems(itemsList);
  }, [itemsList]);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable" direction="horizontal">
        {(provided, snapshot) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            style={getListStyle(snapshot.isDraggingOver)}
          >
            {items.map((item, index) => {
              const isUrl = item?.url || item?.path;
              const file = item?.file ? item.file : item;
              const url = isUrl
                ? isUrl
                : file instanceof Blob
                ? URL.createObjectURL(file)
                : null;

              if (!item) return;
              return (
                <Draggable
                  key={`item-${index}`}
                  draggableId={`item-${index}`}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <ListItem
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={getItemStyle(
                        snapshot.isDragging,
                        provided.draggableProps.style,
                        index
                      )}
                    >
                      <FilePreview
                        item={item}
                        url={url}
                        isUrl={isUrl}
                        error={errors[index]}
                      />

                      <div
                        className={`absolute top-0 ${
                          isRtl ? "left-0" : "right-0"
                        }`}
                      >
                        {isUrl ? (
                          <IconButton
                            component="a"
                            target="_blank"
                            href={url}
                            edge="end"
                            aria-label="delete"
                            size="small"
                          >
                            <GetAppRoundedIcon />
                          </IconButton>
                        ) : (
                          <IconButton
                            component="a"
                            target="_blank"
                            href={url}
                            edge="end"
                            color="primary"
                            aria-label="view"
                            size="small"
                          >
                            <RemoveRedEyeIcon />
                          </IconButton>
                        )}

                        <IconButton
                          disabled={disabled}
                          onClick={() => onDelete(index)}
                          edge="end"
                          aria-label="delete"
                          color={"error"}
                          size="small"
                        >
                          <ClearRoundedIcon />
                        </IconButton>
                        {index === 0 ? (
                          <IconButton color="secondary" size="small">
                            <StarIcon />
                          </IconButton>
                        ) : null}
                        <IconButton color="primary" size="small">
                          <span
                            className="w-6 h-6 ms-1 flex items-center justify-center rounded-full"
                            style={{
                              color: "#fff",
                              backgroundColor: "#0366ff",
                            }}
                          >
                            {index + 1}
                          </span>
                        </IconButton>
                      </div>
                    </ListItem>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default Sortable;
